import { z } from "zod";
import { queryOptions } from "@tanstack/react-query";
import { axiosInstance } from "./axios";
import { logger } from "@/lib/logger";

const matchDataSchema = z.object({
    matchId: z.number(),
    date: z.string(),
    tournamentName: z.string(),
    awayTeam: z.string(),
    awayGoals: z.number(),
    homeTeam: z.string(),
    homeGoals: z.number(),
});

const matchPerformanceSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    total: z.number(),
    average: z.number(),
    matchesPlayed: z.number(),
    tournamentName: z.string().nullable(),
    matchInfo: matchDataSchema.nullable(),
});

const matchGoalAssistPerformanceSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    scores: z.number(),
    matchesPlayed: z.number(),
    tournamentName: z.string().nullable(),
    matchInfo: matchDataSchema.nullable(),
});

const topMatchesResponseSchema = z.object({
    byAverage: z.array(matchPerformanceSchema),
    byTotal: z.array(matchPerformanceSchema),
    goals: z.array(matchGoalAssistPerformanceSchema),
    assists: z.array(matchGoalAssistPerformanceSchema),
    blockedShots: z.array(matchGoalAssistPerformanceSchema),
    matches: z.array(matchDataSchema),
});

export type TopMatchesResponse = z.infer<typeof topMatchesResponseSchema>;
export type MatchPerformance = z.infer<typeof matchPerformanceSchema>;
export type MatchGoalAssistPerformance = z.infer<typeof matchGoalAssistPerformanceSchema>;
export type MatchData = z.infer<typeof matchDataSchema>;

// Tournament KPI Schema
const matchInfoSchema = z.object({
    matchId: z.number(),
    date: z.string(),
    tournamentName: z.string(),
    awayTeam: z.string(),
    awayGoals: z.number(),
    homeTeam: z.string(),
    homeGoals: z.number(),
});

const tournamentPerformanceSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    total: z.number(),
    average: z.number(),
    matchesPlayed: z.number(),
    tournamentName: z.string().nullable(),
    matchInfo: matchInfoSchema.nullable(),
});

const goalAssistPerformanceSchema = z.object({
    playerId: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    scores: z.number(),
    matchesPlayed: z.number(),
    tournamentName: z.string().nullable(),
    matchInfo: matchInfoSchema.nullable(),
});

const topTournamentsResponseSchema = z.object({
    byAverage: z.array(tournamentPerformanceSchema),
    byTotal: z.array(tournamentPerformanceSchema),
    goals: z.array(goalAssistPerformanceSchema),
    assists: z.array(goalAssistPerformanceSchema),
    blockedShots: z.array(goalAssistPerformanceSchema),
    matches: z.array(matchInfoSchema),
});

export type TopTournamentsResponse = z.infer<typeof topTournamentsResponseSchema>;
export type TournamentPerformance = z.infer<typeof tournamentPerformanceSchema>;
export type GoalAssistPerformance = z.infer<typeof goalAssistPerformanceSchema>;
export type MatchInfo = z.infer<typeof matchInfoSchema>;

export const fetchTopMatchPerformances = async (matchIds?: number | number[], teamId?: number): Promise<TopMatchesResponse> => {
    try {
        let matchIdsArray: number[] = [];

        if (typeof matchIds === "number") {
            matchIdsArray = [matchIds];
        } else if (Array.isArray(matchIds)) {
            matchIdsArray = matchIds;
        }

        const response = await axiosInstance.post("/kpis/top/matches", { matchIds: matchIdsArray, teamId });
        logger.log("Match API Response:", response.data);

        if (!response.data) {
            logger.log("Match API returned null/undefined");
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        const parsed = topMatchesResponseSchema.safeParse(response.data);

        if (!parsed.success) {
            logger.error("Match Zod validation failed:", parsed.error.errors);
            logger.log("Actual data structure:", JSON.stringify(response.data, null, 2));
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        return parsed.data;
    } catch (error) {
        logger.error("Parse error:", error);
        throw new Error(`Failed to fetch top match performances: ${error}`);
    }
};

// Fetch top tournament performances with optional tournament ID
export const fetchTopTournamentPerformances = async (tournamentId?: number, teamId?: number): Promise<TopTournamentsResponse> => {
    try {
        const url = tournamentId ? `/kpis/${teamId ?? 0}/top/${tournamentId}` : `/kpis/${teamId ?? 0}/top/tournaments`;

        const response = await axiosInstance.get(url);
        logger.log("Tournament API Response:", response.data);

        if (!response.data) {
            logger.log("Tournament API returned null/undefined");
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        // Try to parse the response
        const parsed = topTournamentsResponseSchema.safeParse(response.data);

        if (!parsed.success) {
            logger.error("Tournament Zod validation failed:", parsed.error.errors);
            logger.log("Actual data structure:", JSON.stringify(response.data, null, 2));
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        return parsed.data;
    } catch (error) {
        logger.error("Tournament fetch error:", error);
        throw new Error(`Failed to fetch top tournament performances: ${error}`);
    }
};

// Fetch top season performances with season year
export const fetchTopSeasonPerformances = async (seasonYear?: number, teamId?: number): Promise<TopTournamentsResponse> => {
    try {
        const year = seasonYear ?? new Date().getFullYear();
        const url = `/kpis/${teamId ?? 0}/top/season/${year}`;

        const response = await axiosInstance.get(url);
        logger.log("Season API Response:", response.data);

        if (!response.data) {
            logger.log("Season API returned null/undefined");
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        const parsed = topTournamentsResponseSchema.safeParse(response.data);

        if (!parsed.success) {
            logger.error("Season Zod validation failed:", parsed.error.errors);
            logger.log("Actual data structure:", JSON.stringify(response.data, null, 2));
            return {
                byAverage: [],
                byTotal: [],
                goals: [],
                assists: [],
                blockedShots: [],
                matches: [],
            };
        }

        return parsed.data;
    } catch (error) {
        logger.error("Season fetch error:", error);
        throw new Error(`Failed to fetch top season performances: ${error}`);
    }
};

// Query options for React Query
export const topMatchPerformancesQueryOptions = (matchIds?: number | number[], teamId?: number) =>
    queryOptions({
        queryKey: ["top-match-performances", matchIds, teamId],
        queryFn: () => fetchTopMatchPerformances(matchIds, teamId),
        staleTime: 5 * 60 * 1000,
    });

export const topTournamentPerformancesQueryOptions = (tournamentId?: number, teamId?: number) =>
    queryOptions({
        queryKey: ["top-tournament-performances", tournamentId, teamId],
        queryFn: () => fetchTopTournamentPerformances(tournamentId, teamId),
        staleTime: 5 * 60 * 1000,
    });

export const topSeasonPerformancesQueryOptions = (seasonYear?: number, teamId?: number) =>
    queryOptions({
        queryKey: ["top-season-performances", seasonYear, teamId],
        queryFn: () => fetchTopSeasonPerformances(seasonYear, teamId),
        staleTime: 5 * 60 * 1000,
    });
